import React from 'react'
import { useGetRegisters } from '@core/react-query/features/registers/registers/hooks'
import { Autocomplete } from '@mui/material'
import { Checkbox } from '@common/Checkbox'
import { Chip, TextField } from '@common/material'
import { localizedStrings } from '@core/strings'

type ComponentType = {
  onChange?: (value: number[] | number) => void
  values?: number[]
  value?: number
  error?: boolean
  readOnly?: boolean
  multiple?: boolean
  closeOnSelect?: boolean
  projectId: number
  organisationId: number
  hiddenOptions?: number[]
  required?: boolean
  registerTemplateId?: number
  includeOrganisationRegisters?: boolean
}

const SelectRegister: React.FC<ComponentType> = ({
  onChange,
  value,
  values,
  error,
  readOnly,
  multiple = false,
  closeOnSelect = true,
  projectId,
  organisationId,
  hiddenOptions = [],
  required,
  registerTemplateId,
  includeOrganisationRegisters,
}) => {
  const { data, isFetching } = useGetRegisters({
    organisationId,
    projectIds: projectId ? [projectId] : null,
    registerTemplateId,
    includeOrganisationRegisters,
    page: 1,
    size: 10000,
  })

  const list =
    data?.pages[0].content
      ?.map((page) => page)
      .flat()
      .filter((register) => register.status === 'LIVE') || []

  return (
    <Autocomplete
      value={multiple ? values : value}
      multiple={multiple}
      size='small'
      disabled={readOnly}
      disableCloseOnSelect={!closeOnSelect}
      loading={isFetching}
      options={
        list
          .map(({ id }) => id)
          .filter((id) => !hiddenOptions.includes(id as number)) || []
      }
      onChange={(event, value: number[] | number) => {
        onChange(value)
      }}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props}>
            {multiple && <Checkbox checked={selected} mr={1} />}
            {list.find(({ id }) => id === option)?.name}
          </li>
        )
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={localizedStrings.register}
            multiline
            required={required}
            error={!!error}
          />
        )
      }}
      getOptionLabel={(o) => {
        return list.find(({ id }) => id === o)?.name || ''
      }}
      renderTags={(value) =>
        value.map((option, index) => (
          <Chip
            key={index}
            size='small'
            variant='filled'
            label={list.find(({ id }) => id === option)?.name || ''}
            onDelete={
              !readOnly
                ? () => {
                    onChange(values.filter((v) => v !== option))
                  }
                : undefined
            }
            sx={{ mr: 0.5, my: 0.5 }}
          />
        ))
      }
    />
  )
}

export default SelectRegister
